export const DEFAULT_VOLUME = 0.75;

const AUDIO_PROGRESS_KEY_PREFIX = "healthos.media-player.audio-progress:";
const VOLUME_PREFERENCE_KEY = "healthos.media-player.volume-preference";

export interface MediaVolumePreference {
  muted: boolean;
  volume: number;
}

export function clampVolume(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export function readMediaVolumePreference(): MediaVolumePreference {
  const fallback = { muted: false, volume: DEFAULT_VOLUME };
  if (typeof window === "undefined") return fallback;

  try {
    const stored = window.localStorage.getItem(VOLUME_PREFERENCE_KEY);
    if (!stored) return fallback;
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== "object" || parsed === null) return fallback;
    const preference = parsed as Partial<MediaVolumePreference>;
    return {
      muted: preference.muted === true,
      volume:
        typeof preference.volume === "number" ? clampVolume(preference.volume) : DEFAULT_VOLUME,
    };
  } catch {
    return fallback;
  }
}

export function saveMediaVolumePreference(preference: MediaVolumePreference) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VOLUME_PREFERENCE_KEY, JSON.stringify(preference));
  } catch {
    // Playback continues if storage is unavailable or full.
  }
}

function audioProgressKey(audioUrl: string): string {
  return `${AUDIO_PROGRESS_KEY_PREFIX}${encodeURIComponent(audioUrl)}`;
}

export function readAudioProgress(audioUrl: string): number {
  if (typeof window === "undefined") return 0;
  try {
    const stored = window.localStorage.getItem(audioProgressKey(audioUrl));
    if (!stored) return 0;
    const progress = Number(stored);
    return Number.isFinite(progress) && progress > 0 ? progress : 0;
  } catch {
    return 0;
  }
}

export function saveAudioProgress(audioUrl: string, currentTime: number) {
  if (typeof window === "undefined" || !Number.isFinite(currentTime) || currentTime <= 0) return;
  try {
    window.localStorage.setItem(audioProgressKey(audioUrl), String(currentTime));
  } catch {
    // Playback continues if storage is unavailable or full.
  }
}

export function clearAudioProgress(audioUrl: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(audioProgressKey(audioUrl));
  } catch {
    // Playback continues if storage is unavailable or full.
  }
}
